import React, { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { Layout, PageHeader } from "@/components/Layout";
import { fmtRM } from "@/lib/format";
import { StatusBadge, RiskBadge } from "@/components/StatusBadge";
import { PeriodSelector, periodLabel, periodParams } from "@/components/PeriodSelector";
import { ArrowLeft, Activity, MoonStar, Banknote } from "lucide-react";

const FILTERS = [
  { key: "active", label: "Active (≤90d)" },
  { key: "inactive", label: "Inactive (90+d)" },
  { key: "all", label: "All" },
];

function Stat({ label, value, sub, icon: Icon, accent }) {
  return (
    <div className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm">
      <div className="flex items-start justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-slate-500">{label}</span>
        {Icon && <div className={`h-8 w-8 rounded-md flex items-center justify-center ${accent}`}><Icon className="h-4 w-4" /></div>}
      </div>
      <div className="mt-3 font-mono-fig text-2xl font-bold text-slate-900">{value}</div>
      {sub && <div className="text-xs text-slate-400 mt-1">{sub}</div>}
    </div>
  );
}

export default function Outstanding() {
  const [params, setParams] = useSearchParams();
  const period = params.get("period") || "all";
  const value = params.get("value") || null;
  const filter = params.get("filter") || "active";
  const [data, setData] = useState(null);

  useEffect(() => {
    api.get("/dashboard/outstanding", { params: periodParams(period, value) }).then((r) => setData(r.data));
  }, [period, value]);

  const pLabel = periodLabel(period, value);
  const setQuery = (next) => {
    const base = { period, filter };
    if (value) base.value = value;
    setParams({ ...base, ...next });
  };
  const onPeriodChange = (p, v) => {
    const next = { period: p, filter };
    if (v) next.value = v;
    setParams(next);
  };

  const rows = (data?.loans || []).filter((l) =>
    filter === "all" ? true : filter === "active" ? l.active : !l.active
  );

  return (
    <Layout>
      <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900 mb-4">
        <ArrowLeft className="h-4 w-4" /> Back to dashboard
      </Link>

      <PageHeader
        title="Outstanding Balances"
        subtitle={`Customer balances & last payment activity · ${pLabel}`}
        action={<PeriodSelector period={period} value={value} onChange={onPeriodChange} testid="outstanding-period-selector" />}
      />

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 lg:gap-6 mb-6">
        <Stat label="Total Outstanding" value={fmtRM((data?.activeTotal || 0) + (data?.inactiveTotal || 0))} sub={`${(data?.activeCount || 0) + (data?.inactiveCount || 0)} loans`} icon={Banknote} accent="bg-slate-100 text-slate-700" />
        <Stat label="Active Outstanding" value={fmtRM(data?.activeTotal)} sub={`${data?.activeCount || 0} loans · paid ≤90d`} icon={Activity} accent="bg-green-100 text-green-700" />
        <Stat label="Inactive Outstanding" value={fmtRM(data?.inactiveTotal)} sub={`${data?.inactiveCount || 0} loans · dormant 90+d`} icon={MoonStar} accent="bg-red-100 text-red-700" />
      </div>

      <div className="flex items-center gap-1 bg-slate-100 rounded-md p-1 w-fit mb-4" data-testid="outstanding-filter">
        {FILTERS.map((f) => (
          <button
            key={f.key}
            data-testid={`out-filter-${f.key}`}
            onClick={() => setQuery({ filter: f.key })}
            className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              filter === f.key ? "bg-white shadow-sm text-slate-900" : "text-slate-500 hover:text-slate-800"
            }`}
          >
            {f.label}
          </button>
        ))}
      </div>

      <div className="bg-white border border-slate-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="w-full text-sm" data-testid="outstanding-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 bg-slate-50">
              <th className="px-5 py-3 font-medium">Code</th>
              <th className="px-3 py-3 font-medium">Customer</th>
              <th className="px-3 py-3 font-medium">Applied</th>
              <th className="px-3 py-3 font-medium">Last Payment</th>
              <th className="px-3 py-3 font-medium text-right">Days Since</th>
              <th className="px-3 py-3 font-medium text-right">Outstanding</th>
              <th className="px-5 py-3 font-medium">Activity</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((l) => (
              <tr key={l.code} data-testid={`out-row-${l.code}`} className="border-b border-slate-50 hover:bg-slate-50">
                <td className="px-5 py-3"><Link to={`/loans/${l.code}`} className="font-mono-fig font-medium text-slate-900 hover:underline">{l.code}</Link></td>
                <td className="px-3 py-3 text-slate-700">{l.customerName}</td>
                <td className="px-3 py-3 text-slate-500 whitespace-nowrap">{l.dateApplied}</td>
                <td className="px-3 py-3 text-slate-600 whitespace-nowrap">{l.lastPaymentDate || <span className="text-slate-300">No payment</span>}</td>
                <td className={`px-3 py-3 text-right font-mono-fig ${l.active ? "text-slate-600" : "text-red-600 font-semibold"}`}>{l.daysSinceLastActivity != null ? `${l.daysSinceLastActivity}d` : "—"}</td>
                <td className="px-3 py-3 text-right font-mono-fig font-semibold text-slate-900">{fmtRM(l.outstanding)}</td>
                <td className="px-5 py-3">
                  {l.active ? (
                    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border bg-green-50 text-green-700 border-green-200"><Activity className="h-3 w-3" /> Active</span>
                  ) : (
                    <span className="inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-semibold border bg-red-50 text-red-700 border-red-200"><MoonStar className="h-3 w-3" /> Inactive</span>
                  )}
                </td>
              </tr>
            ))}
            {data && rows.length === 0 && (
              <tr><td colSpan={7} className="px-5 py-12 text-center text-slate-400">No loans in this view.</td></tr>
            )}
          </tbody>
        </table>
      </div>
    </Layout>
  );
}
